import { createApiInstance } from './axios-config';
import { useAuthStore } from '../../store/authStore';

const api = createApiInstance();

export interface Channel {
  id: string;
  title: string;
  slug: string;
  description?: string;
  is_public: boolean;
  creator_id: string;
  created_at: string;
  member_count: number;
  is_joined: boolean;
}

export interface DMConversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  other_user_id: string;
  other_first_name: string;
  other_last_name: string;
  other_email: string;
  unread_count: number;
  has_mention: boolean;
}

export interface Participant {
  id: string;
  user_id: string;
  role: 'creator' | 'admin' | 'member';
  joined_at: string;
  last_read_at: string;
  notifications_enabled: boolean;
  is_muted: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface OrgUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

class MessagingService {
  // --- Channels ---

  async getChannels(): Promise<Channel[]> {
    const response = await api.get('/api/v1/channels');
    return response.data.channels;
  }

  async joinChannel(channelId: string): Promise<void> {
    await api.post(`/api/v1/channels/${channelId}/join`);
  }

  async leaveChannel(channelId: string): Promise<void> {
    await api.post(`/api/v1/channels/${channelId}/leave`);
  }

  // --- DMs ---

  async findOrCreateDM(targetUserId: string): Promise<{ conversationId: string; existing: boolean }> {
    const response = await api.post('/api/v1/dms', { targetUserId });
    return response.data;
  }

  async getDMs(): Promise<DMConversation[]> {
    const response = await api.get('/api/v1/dms');
    return response.data.dms;
  }

  // --- Participants ---

  async getParticipants(conversationId: string): Promise<Participant[]> {
    const response = await api.get(`/api/v1/conversations/${conversationId}/participants`);
    return response.data.participants;
  }

  async addParticipants(conversationId: string, userIds: string[]): Promise<void> {
    await api.post(`/api/v1/conversations/${conversationId}/participants`, { userIds });
  }

  async removeParticipant(conversationId: string, userId: string): Promise<void> {
    await api.delete(`/api/v1/conversations/${conversationId}/participants/${userId}`);
  }

  async updateParticipantRole(conversationId: string, userId: string, role: string): Promise<void> {
    await api.patch(`/api/v1/conversations/${conversationId}/participants/${userId}`, { role });
  }

  async leaveConversation(conversationId: string): Promise<void> {
    await api.post(`/api/v1/conversations/${conversationId}/leave`);
  }

  // --- Conversations (new types) ---

  async createGroupChat(title: string, participantIds: string[], historyVisible: boolean = true): Promise<any> {
    const response = await api.post('/api/v1/conversations', {
      title,
      conversation_type: 'group',
      participant_ids: participantIds,
      history_visible_to_new_members: historyVisible,
    });
    return response.data;
  }

  async createChannel(title: string, slug: string, description?: string, isPublic: boolean = true): Promise<any> {
    const response = await api.post('/api/v1/conversations', {
      title,
      conversation_type: 'channel',
      slug,
      description,
      is_public: isPublic,
    });
    return response.data;
  }

  // --- Org Users (for user picker) ---

  async searchOrgUsers(query: string): Promise<OrgUser[]> {
    // Get organizationId from auth store
    const organizationId = useAuthStore.getState().user?.organizationId;
    if (!organizationId) {
      console.warn('No organizationId available for user search');
      return [];
    }

    const response = await api.get('/api/v1/users/search', {
      params: { query, organizationId },
    });

    // Backend returns flat array with full_name; transform to first_name/last_name
    const users = Array.isArray(response.data) ? response.data : (response.data.users || []);
    return users.map((u: any) => {
      const nameParts = (u.full_name || '').trim().split(/\s+/);
      return {
        id: u.id,
        email: u.email,
        first_name: u.first_name || nameParts[0] || '',
        last_name: u.last_name || nameParts.slice(1).join(' ') || '',
      };
    });
  }

  // --- Unread ---

  async markConversationRead(conversationId: string): Promise<void> {
    // This is handled via WebSocket (mark:read event), but also available via REST
    await api.post(`/api/v1/conversations/${conversationId}/read`).catch(() => {
      // Silently fail - WebSocket mark:read is the primary mechanism
    });
  }

  // --- Org Agents ---

  async getOrgAgents(): Promise<any[]> {
    const response = await api.get('/api/v1/agents/org');
    return response.data.agents;
  }

  // --- Presence ---

  async getOnlineUsers(): Promise<string[]> {
    const response = await api.get('/api/v1/presence/online');
    return response.data.users || [];
  }
}

export const messagingService = new MessagingService();
